import { Inject, Injectable } from '@angular/core';

import { Subject } from 'rxjs';
import { debounceTime, groupBy, mergeMap } from 'rxjs/operators';

import type { CurrentUser } from '@recruitee/account-types';
import { ADMIN, COMPANY_ID, LocalStorageService } from '@recruitee/common';
import type { MailboxFormMessageData, MailboxNewMessageAction } from '@recruitee/mailbox-types';

@Injectable({ providedIn: 'root' })
export class MailboxCache {
  private formChanges$ = new Subject<{
    key: string;
    action: MailboxNewMessageAction | null | undefined;
    formValues: MailboxFormMessageData;
  }>();

  constructor(
    private localStorageService: LocalStorageService,
    @Inject(COMPANY_ID) private companyId: number,
    @Inject(ADMIN) private admin: CurrentUser,
  ) {
    this.handleFutureFormChanges();
  }

  public publish({
    key,
    action,
    formValues,
  }: {
    key: string;
    action: MailboxNewMessageAction | null | undefined;
    formValues: MailboxFormMessageData;
  }): void {
    // Don't update cache if browser tab is not active.
    // This fixes case where user has multiple recruitee tabs opened. In first tab there is a form
    // with a draft message. When in another tab said draft is sent, first one will keep putting
    // it back into local storage, and propagating it to other tabs.
    if (!document.hasFocus()) return;
    this.formChanges$.next({ key, action, formValues });
  }

  public get(key: string): { action: MailboxNewMessageAction; formValues: any } {
    return this.localStorageService.get(key);
  }

  public remove(key: string): void {
    return this.localStorageService.remove(key);
  }

  public key(kind: string, id: number | null = null): string {
    return ['mailbox', kind, id, this.companyId, this.admin.id].filter(Boolean).join('.');
  }

  private handleFutureFormChanges(): void {
    this.formChanges$
      .asObservable()
      .pipe(
        groupBy(data => data['key']),
        mergeMap(obs => obs.pipe(debounceTime(50))),
      )
      .subscribe(data => {
        this.localStorageService.set(data['key'], {
          action: data['action'],
          formValues: data['formValues'],
        });
      });
  }
}
